(function () {
  const BODY_CLASS = 'ms-pf-open';

  class MindsightProductFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enabled === 'false') return;

      this.config = this.#parseConfig();
      this.answers = { q1: null, q2: null, q3: null };
      this.history = [];
      this.currentStep = 'intro';
      this.resultKey = null;

      this.$progressFill = this.querySelector('[data-pf-progress]');
      this.$progressLabel = this.querySelector('[data-pf-progress-label]');
      this.$result = this.querySelector('[data-pf-result]');
      this.$collectionResult = this.querySelector('[data-pf-collection-result]');
      this.$emailBlock = this.querySelector('[data-pf-email-block]');
      this.$emailDesc = this.querySelector('[data-pf-email-desc]');
      this.$emailForm = this.querySelector('[data-pf-email-form]');
      this.$emailSuccess = this.querySelector('[data-pf-email-success]');
      this.$emailError = this.querySelector('[data-pf-email-error]');
      this.$checkout = this.querySelector('[data-pf-checkout]');
      this.$resultLink = this.querySelector('[data-pf-result-link]');
      this.currentProduct = null;

      this.#bindEvents();
      this.#checkHash();

      if (window.location.hash === '#find-your-match') {
        this.open();
      }
    }

    #parseConfig() {
      const node = this.querySelector('[data-pf-config]');
      if (!node) return { products: {}, results: {} };
      try {
        return JSON.parse(node.textContent);
      } catch {
        return { products: {}, results: {} };
      }
    }

    #bindEvents() {
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-product-finder-open], .ms-pf-open, a[href="#find-your-match"]');
        if (!trigger) return;
        e.preventDefault();
        this.open();
      });

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-pf-close]')) {
          e.preventDefault();
          this.close();
          return;
        }

        if (e.target.closest('[data-pf-next]')) {
          e.preventDefault();
          this.#goTo('q1');
          return;
        }

        if (e.target.closest('[data-pf-retake]')) {
          e.preventDefault();
          this.#reset();
          return;
        }

        if (e.target.closest('[data-pf-result-link]')) {
          e.preventDefault();
          this.#viewProduct();
          return;
        }

        if (e.target.closest('[data-pf-checkout]')) {
          e.preventDefault();
          this.#goToCheckout();
          return;
        }

        const answerBtn = e.target.closest('[data-pf-answer]');
        if (answerBtn) {
          e.preventDefault();
          this.#selectAnswer(answerBtn);
        }
      });

      this.$emailForm?.addEventListener('click', (e) => {
        e.stopPropagation();
      });

      this.$emailForm?.addEventListener('mousedown', (e) => {
        e.stopPropagation();
      });

      this.querySelector('[data-pf-email-input]')?.addEventListener('keydown', (e) => {
        e.stopPropagation();
      });

      this.$emailForm?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.#submitEmail();
      });

      document.addEventListener('keydown', (e) => {
        if (!this.classList.contains('is-open')) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close();
        }
      });

      window.addEventListener('hashchange', () => this.#checkHash());
    }

    #checkHash() {
      if (window.location.hash === '#find-your-match' && !this.classList.contains('is-open')) {
        this.open(false);
      }
    }

    open(updateHash = true) {
      this.removeAttribute('hidden');
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      document.body.classList.add(BODY_CLASS);

      if (updateHash && window.location.hash !== '#find-your-match') {
        history.pushState(null, '', '#find-your-match');
      }

      const focusTarget = this.querySelector('.ms-pf__step.is-active button, .ms-pf__step.is-active .ms-pf__start');
      focusTarget?.focus();
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('hidden', '');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove(BODY_CLASS);

      if (window.location.hash === '#find-your-match') {
        history.pushState(null, '', window.location.pathname + window.location.search);
      }
    }

    #reset() {
      this.answers = { q1: null, q2: null, q3: null };
      this.history = [];
      this.resultKey = null;
      this.currentProduct = null;
      this.$emailForm?.reset();
      this.$emailSuccess?.setAttribute('hidden', '');
      this.$emailError?.setAttribute('hidden', '');
      this.$emailForm?.removeAttribute('hidden');
      this.$checkout?.setAttribute('hidden', '');
      this.#goTo('intro', false);
    }

    #selectAnswer(btn) {
      const key = btn.dataset.pfAnswer;
      const value = btn.dataset.value;
      this.answers[key] = value;

      if (key === 'q1') {
        const q2Map = {
          screen: 'q2-screen',
          focus: 'q2-focus',
          habit: 'q2-habit',
          sleep: 'q2-sleep',
          calm: 'q2-calm',
          gift: 'q2-gift',
        };
        this.#goTo(q2Map[value]);
        return;
      }

      if (key === 'q2') {
        if (this.#needsQ3()) {
          this.#goTo('q3');
        } else {
          this.#showResult();
        }
        return;
      }

      if (key === 'q3') {
        this.#showResult();
      }
    }

    #needsQ3() {
      const { q1, q2 } = this.answers;
      if (q1 === 'focus' || q1 === 'habit') return true;
      if (q1 === 'screen' && q2 === 'phone') return true;
      return false;
    }

    #totalQuestionSteps() {
      const { q1, q2 } = this.answers;
      if (['sleep', 'calm', 'gift'].includes(q1)) return 2;
      if (q1 === 'screen') {
        if (q2 && q2 !== 'phone') return 2;
        return 3;
      }
      if (q1 === 'focus' || q1 === 'habit') return 3;
      return 3;
    }

    #currentQuestionIndex() {
      if (this.currentStep === 'intro') return 0;
      if (this.currentStep === 'q1') return 1;
      if (this.currentStep.startsWith('q2-')) return 2;
      if (this.currentStep === 'q3') return 3;
      if (this.currentStep === 'result') return this.#totalQuestionSteps();
      return 0;
    }

    #updateProgress() {
      const total = this.#totalQuestionSteps();
      const current = this.#currentQuestionIndex();
      const pct =
        this.currentStep === 'intro' ? 0 : Math.min(100, Math.round((current / total) * 100));

      if (this.$progressFill) this.$progressFill.style.width = `${pct}%`;
      if (this.$progressLabel) {
        this.$progressLabel.textContent =
          this.currentStep === 'intro' || this.currentStep === 'result' ? '' : `${current} of ${total}`;
      }
    }

    #goTo(stepId, pushHistory = true) {
      if (pushHistory && this.currentStep) {
        this.history.push(this.currentStep);
      }

      this.querySelectorAll('.ms-pf__step').forEach((el) => {
        el.classList.toggle('is-active', el.dataset.pfStep === stepId);
        el.classList.remove('is-entering');
      });

      const active = this.querySelector(`[data-pf-step="${stepId}"]`);
      if (active) {
        requestAnimationFrame(() => active.classList.add('is-entering'));
      }

      this.currentStep = stepId;
      this.#updateProgress();

      if (stepId !== 'result') {
        const focusEl = active?.querySelector('button:not([hidden]), [data-pf-answer]');
        focusEl?.focus();
      }
    }

    #computeResultKey() {
      const { q1, q2, q3 } = this.answers;

      if (q1 === 'screen') {
        if (q2 === 'multiple' || q2 === 'small') return 'timed-lock-box-phone';
        if (q2 === 'phone') {
          if (q3 === 'compact') return 'phone-lock-box';
          return 'timed-lock-box-phone';
        }
      }

      if (q1 === 'focus') {
        if (q3 === 'compact') return 'phone-lock-box';
        return 'timed-lock-box';
      }

      if (q1 === 'habit') {
        if (q3 === 'compact') return 'phone-lock-box';
        return 'timed-lock-box-habit';
      }

      if (q1 === 'sleep') {
        return q2 === 'tracking' ? 'driftband' : 'breathing-buddha';
      }

      if (q1 === 'calm') {
        if (q2 === 'desk') return 'mindsculpt-focus-stress-relief';
        if (q2 === 'living') return 'kinetic-wall-art';
        return 'calming-cloud-tabletop-fountain';
      }

      if (q1 === 'gift') {
        if (q2 === 'scrolls') return 'timed-lock-box-gift';
        if (q2 === 'calm') return 'breathing-buddha-gift';
        return 'best-sellers';
      }

      return 'timed-lock-box';
    }

    #showResult() {
      this.resultKey = this.#computeResultKey();
      const meta = this.config.results[this.resultKey] || {};
      const isCollection = this.resultKey === 'best-sellers';
      const handle = meta.handle || this.resultKey;

      this.$result?.toggleAttribute('hidden', isCollection);
      this.$collectionResult?.toggleAttribute('hidden', !isCollection);

      if (isCollection) {
        if (this.$emailDesc && meta.guideTitle) {
          this.$emailDesc.textContent = meta.guideTitle;
        }
        this.#goTo('result', false);
        return;
      }

      const product = this.config.products[handle] || {};
      this.currentProduct = product.available ? product : null;

      const img = this.querySelector('[data-pf-result-image]');
      const title = this.querySelector('[data-pf-result-title]');
      const price = this.querySelector('[data-pf-result-price]');
      const blurb = this.querySelector('[data-pf-result-blurb]');
      const quote = this.querySelector('[data-pf-result-quote]');

      if (img) {
        img.src = product.image || '';
        img.alt = product.title || '';
        img.toggleAttribute('hidden', !product.image);
      }
      if (title) title.textContent = product.title || handle;
      if (price) price.textContent = product.price || '';
      if (blurb) blurb.textContent = meta.blurb || '';
      if (quote) {
        if (meta.quote) {
          quote.textContent = `"${meta.quote}"`;
          quote.removeAttribute('hidden');
        } else {
          quote.setAttribute('hidden', '');
        }
      }

      const productUrl = product.url || `/products/${handle}`;
      if (this.$resultLink) {
        this.$resultLink.dataset.url = productUrl;
      }

      const canPurchase = Boolean(product.variantId && product.available);
      this.$checkout?.toggleAttribute('hidden', !canPurchase);

      if (this.$emailDesc && meta.guideTitle) {
        this.$emailDesc.textContent = meta.guideTitle;
      }

      this.#goTo('result', false);
    }

    #viewProduct() {
      const url = this.$resultLink?.dataset.url || this.currentProduct?.url;
      if (!url || url === '#') return;
      this.close();
      window.location.assign(url);
    }

    async #goToCheckout() {
      const variantId = this.currentProduct?.variantId;
      if (!variantId) return;

      const btn = this.$checkout;
      const original = btn?.textContent;
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Redirecting…';
      }

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });

        if (!response.ok) throw new Error('Add to cart failed');

        window.location.href = `${root}checkout`;
      } catch {
        if (btn) {
          btn.textContent = 'Try again';
          btn.disabled = false;
          setTimeout(() => {
            btn.textContent = original;
          }, 1500);
        }
      }
    }

    async #submitEmail() {
      const input = this.$emailForm?.querySelector('input[type="email"]');
      const email = input?.value?.trim();
      if (!email || !input?.checkValidity()) {
        input?.focus();
        return;
      }

      const meta = this.config.results[this.resultKey] || {};
      const useCase = meta.useCase || 'USE_CASE_PHONE';
      const listId = this.dataset.klaviyoListId || 'S9B8tD';

      this.$emailError?.setAttribute('hidden', '');

      try {
        window._learnq = window._learnq || [];
        window._learnq.push([
          'identify',
          {
            $email: email,
            popup_use_case: useCase,
            popup_source: 'product_finder',
          },
        ]);
        window._learnq.push([
          'track',
          'Product Finder Guide Signup',
          {
            popup_use_case: useCase,
            popup_source: 'product_finder',
            guide_url: meta.guideUrl || '',
          },
        ]);

        await this.#subscribeKlaviyo(email, useCase, listId);

        this.$emailForm?.setAttribute('hidden', '');
        this.$emailSuccess?.removeAttribute('hidden');
      } catch {
        this.$emailError?.removeAttribute('hidden');
      }
    }

    async #subscribeKlaviyo(email, useCase, listId) {
      const companyId = window.__klaviyoCompanyId;
      if (!companyId) return;

      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-07-15',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      properties: {
                        popup_use_case: useCase,
                        popup_source: 'product_finder',
                      },
                    },
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: listId } },
              },
            },
          }),
        }
      );

      if (!response.ok && response.status !== 202) {
        throw new Error('Klaviyo subscribe failed');
      }
    }
  }

  if (!customElements.get('mindsight-product-finder')) {
    customElements.define('mindsight-product-finder', MindsightProductFinder);
  }
})();
